import React, { Suspense, useRef } from 'react'
import styled from 'styled-components'
import { Card } from 'components/common/card'
import { Modal } from 'components/common/modal'
import { Column, Row } from 'components/common/layout'
import { usePreAnalysis, useRequestFormOptions } from 'apis/hooks'
import { ScoutCalculatorStore } from 'stores/scouts/scout-calculator'
import { observer } from 'mobx-react'
import { ExpectedProposal } from 'components/scouts/request-form/condition/expected-proposal'
import { PreAnalysis } from 'components/scouts/request-form/condition/pre-analysis'
import { CTAButton } from 'components/scouts/greeting/cta-button'
import { CalculatingProgress } from 'components/scouts/greeting/calculating-progress'

export const DesktopScoutCalculated: React.FC<{ store: ScoutCalculatorStore }> =
  observer(({ store }) => {
    const { data: preAnalysis } = usePreAnalysis({
      positions: store.positionTypeIds,
      experience: store.experience,
    })
    const { data: options } = useRequestFormOptions()
    if (!preAnalysis || !options) return <CalculatedPlaceholder />
    return (
      <Row style={{ justifyContent: 'space-between', margin: '80px 0 40px' }}>
        <Column>
          <PreAnalysis user={store.user} preAnalysis={preAnalysis} />
        </Column>
        <Card
          style={{
            width: 600,
            height: 600,
            padding: 24,
            gap: 24,
            overflowY: 'scroll',
            justifyContent: 'flex-start',
            boxShadow: '2px 4px 30px 0px rgba(0, 0, 0, 0.25)',
          }}
        >
          <ExpectedProposal user={store.user} options={options} />
        </Card>
      </Row>
    )
  })

const Calculated: React.FC<{ store: ScoutCalculatorStore }> = observer(
  ({ store }) => {
    const { data: preAnalysis } = usePreAnalysis({
      positions: store.positionTypeIds,
      experience: store.experience,
    })
    const { data: options } = useRequestFormOptions()
    if (!preAnalysis || !options) return <CalculatedPlaceholder />
    return (
      <>
        <PreAnalysis user={store.user} preAnalysis={preAnalysis} />
        <ExpectedProposal user={store.user} options={options} />
        <div style={{ position: 'sticky', bottom: 24 }}>
          <CTAButton />
        </div>
      </>
    )
  },
)

export const ScoutCalculatorModal: React.FC<{ onClose: () => void }> = observer(
  ({ onClose }) => {
    const store = useRef(new ScoutCalculatorStore()).current
    return (
      <Modal width={630} onClose={onClose} disableBackgroundClick>
        <CalculatorCard>
          {store.isCalculated ? (
            <Suspense fallback={<CalculatedPlaceholder />}>
              <Calculated store={store} />
            </Suspense>
          ) : (
            <Suspense>
              <CalculatingProgress store={store} />
            </Suspense>
          )}
        </CalculatorCard>
      </Modal>
    )
  },
)

const CalculatorCard = styled(Card)`
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 24px 16px;
    gap: 16px;
  }
  @media ${(p) => p.theme.deviceSize.desktop} {
    padding: 24px 32px;
    gap: 32px;
  }
`

const CalculatedPlaceholder = styled(Column)`
  min-height: 100vh;
`
